import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import type { Activity, ActivityKind, CompletedFilters } from "@/api/queries";
import { useCompletedActivities, useCompletedSummary } from "@/api/queries";
import { DetalleEvento } from "@/agenda/DetalleEvento";
import { CHANNEL_BAR, channelOf } from "@/agenda/model";
import { useMe } from "@/auth/useMe";
import { Pagination } from "@/components/Pagination";
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  Page,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { useAgents } from "@/leads/queries";
import { formatDate, formatDateTime, formatTime } from "@/lib/format";

/**
 * *Actividades* — every completed follow-up, as a list.
 *
 * The agenda's other half. The agenda is what is left to do, drawn as a week;
 * this is what got done, read newest first a page at a time, and dated by when
 * each row was written (`created`) rather than by its reminder. Same endpoint,
 * read by authorship: a manager reads what every seller wrote and a seller
 * reads what they wrote themselves — not what they hold, which after a claim
 * includes their predecessors' work. The server enforces it, so the agent
 * picker below is a filter and never a way round it.
 *
 * Every filter lives in the URL, so a manager can send "the visits Ana closed
 * in September" as a link.
 */

/**
 * Every kind a history can hold, in integer order. All sixteen, as the
 * timeline offers them: completed work goes back fifteen years and includes
 * kinds nobody creates by hand any more.
 */
const KINDS = Array.from({ length: 16 }, (_, kind) => kind) as ActivityKind[];

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A `YYYY-MM-DD` off the URL, or empty. A stale bookmark is not a 400. */
function parseDay(raw: string | null): string {
  if (!raw || !DAY.test(raw)) return "";
  return Number.isNaN(new Date(`${raw}T12:00`).getTime()) ? "" : raw;
}

function parseKind(raw: string | null): ActivityKind | null {
  if (!raw) return null;
  const kind = Number(raw) as ActivityKind;
  return KINDS.includes(kind) ? kind : null;
}

function parseAgent(raw: string | null): string {
  return raw && /^\d+$/.test(raw) ? raw : "";
}

export function Actividades() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const [openEvent, setOpenEvent] = useState<Activity | null>(null);

  const isManager = me?.is_manager ?? false;
  const from = parseDay(params.get("from"));
  const to = parseDay(params.get("to"));
  const kind = parseKind(params.get("kind"));
  // A seller's reach is already their own, so their picker has one name and
  // nothing to send. Ignoring a hand-edited `?agent=` rather than sending it
  // keeps the list from going blank on a URL a manager shared.
  const agent = isManager ? parseAgent(params.get("agent")) : "";
  const page = Math.max(1, Number(params.get("page") ?? 1) || 1);
  const filtered = Boolean(from || to || kind !== null || agent);

  const filters: CompletedFilters = {
    from,
    to,
    kind,
    agent,
    scope: isManager ? "all" : "authored",
  };
  const { data, isPending, isError, isFetching, refetch } = useCompletedActivities(
    filters,
    page,
  );
  const agents = useAgents(isManager);

  /** One `setParams` per gesture; any filter change goes back to page 1. */
  function update(changes: Record<string, string>) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      if (!("page" in changes)) next.delete("page");
      return next;
    });
  }

  return (
    <Page
      title={t("activities.title")}
      subtitle={
        data
          ? t("activities.count", { count: data.count.toLocaleString("es-MX") })
          : undefined
      }
    >
      <div className="mb-5 flex flex-wrap items-center gap-2.5">
        {/* Visible labels, not `aria-label`: an empty date input does not say
            what it is, where a select's first option does. */}
        <label className="flex items-center gap-2 text-[13px] text-mute-600">
          {t("activities.from")}
          <Input
            type="date"
            value={from}
            max={to || undefined}
            className="w-auto"
            onChange={(event) => update({ from: event.target.value })}
          />
        </label>
        <label className="flex items-center gap-2 text-[13px] text-mute-600">
          {t("activities.to")}
          <Input
            type="date"
            value={to}
            min={from || undefined}
            className="w-auto"
            onChange={(event) => update({ to: event.target.value })}
          />
        </label>

        <Select
          value={kind ?? ""}
          aria-label={t("activities.filterKind")}
          className="w-auto"
          onChange={(event) => update({ kind: event.target.value })}
        >
          <option value="">{t("activities.allKinds")}</option>
          {KINDS.map((option) => (
            <option key={option} value={option}>
              {t(`activityKind.${option}`)}
            </option>
          ))}
        </Select>

        {isManager ? (
          <Select
            value={agent}
            aria-label={t("activities.filterAgent")}
            className="w-auto"
            onChange={(event) => update({ agent: event.target.value })}
          >
            <option value="">{t("activities.allAgents")}</option>
            {(agents.data ?? []).map((option) => (
              <option key={option.id} value={String(option.id)}>
                {option.name || `#${option.id}`}
              </option>
            ))}
          </Select>
        ) : (
          // A seller reads only their own work, so the one name they may pick
          // is theirs. Shown rather than hidden: the filter is the same on
          // every account, and what differs is whose names it holds.
          <Select
            value={String(me?.id ?? "")}
            aria-label={t("activities.filterAgent")}
            className="w-auto"
            disabled
          >
            <option value={String(me?.id ?? "")}>{me?.name || me?.email}</option>
          </Select>
        )}

        {filtered ? (
          <Button
            variant="ghost"
            onClick={() => update({ from: "", to: "", kind: "", agent: "" })}
          >
            {t("activities.clearFilters")}
          </Button>
        ) : null}
      </div>

      <KindSummary filters={filters} />

      {isPending ? (
        <Spinner label={t("activities.loading")} />
      ) : isError ? (
        <ErrorState
          message={t("activities.loadFailed")}
          onRetry={() => void refetch()}
        />
      ) : data.results.length === 0 ? (
        <EmptyState>
          {filtered ? t("activities.emptyFiltered") : t("activities.empty")}
        </EmptyState>
      ) : (
        <>
          {/* Two renderings of the same rows, one per width, as the prospect
              list does: six columns and an action do not survive a phone, so
              it gets cards rather than a table that scrolls sideways. From
              `md`, not `sm`: at 640px the comment column is a word wide. */}
          <div className="hidden md:block">
            <Table>
              <thead>
                <tr>
                  <th className="lg:w-[160px]">{t("activities.column.date")}</th>
                  <th className="w-[18%]">{t("activities.column.kind")}</th>
                  <th className="w-[22%]">{t("activities.column.prospect")}</th>
                  {isManager ? (
                    <th className="w-[16%]">{t("activities.column.agent")}</th>
                  ) : null}
                  <th>{t("activities.column.comment")}</th>
                  <th className="lg:w-[160px]">{t("activities.column.dueAt")}</th>
                  <th className="w-px">
                    <span className="sr-only">{t("activities.open")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((activity) => (
                  <tr key={activity.id}>
                    <td className="text-[13px] tabular-nums">
                      <When iso={activity.created} />
                    </td>
                    <td>
                      <KindLabel activity={activity} />
                    </td>
                    <td>
                      <Prospect activity={activity} />
                    </td>
                    {isManager ? (
                      <td className="text-[13px]">
                        <AgentName activity={activity} />
                      </td>
                    ) : null}
                    <td className="text-[13px]">
                      <Comment activity={activity} />
                    </td>
                    {/* Last, after everything about the work itself: `due_at`
                        is the reminder it was booked under, not when it was
                        done — the first column and the date range are
                        `created`. */}
                    <td className="text-[13px] text-mute-600 tabular-nums">
                      {/* Empty on what a call or an email logged: it was
                          never booked, so there is no reminder to show. */}
                      {activity.due_at ? (
                        <When iso={activity.due_at} />
                      ) : (
                        t("common.none")
                      )}
                    </td>
                    <td className="whitespace-nowrap">
                      <OpenButton activity={activity} onOpen={setOpenEvent} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="border-t-2 border-rule md:hidden">
            {data.results.map((activity) => (
              <article key={activity.id} className="border-b border-rule py-3.5">
                <div className="flex items-start justify-between gap-2.5">
                  <KindLabel activity={activity} />
                  <span className="shrink-0 text-[11px] text-mute-600 tabular-nums">
                    {formatDateTime(activity.created)}
                  </span>
                </div>
                <div className="mt-1.5">
                  <Prospect activity={activity} />
                </div>
                <div className="mt-1.5 text-[13px]">
                  <Comment activity={activity} />
                </div>
                <div className="mt-2.5 flex items-end justify-between gap-2.5 border-t border-rule pt-2.5">
                  <div className="text-[11px] text-mute-600">
                    {isManager ? (
                      <div className="tracking-[0.06em] uppercase">
                        <AgentName activity={activity} />
                      </div>
                    ) : null}
                    {activity.due_at ? (
                      <div className="tabular-nums">
                        {t("activities.dueAt", {
                          when: formatDateTime(activity.due_at),
                        })}
                      </div>
                    ) : null}
                  </div>
                  <OpenButton activity={activity} onOpen={setOpenEvent} />
                </div>
              </article>
            ))}
          </div>

          <Pagination
            page={page}
            count={data.count}
            shown={data.results.length}
            isFetching={isFetching}
            onChange={(next) => update({ page: String(next) })}
          />
        </>
      )}

      <DetalleEvento event={openEvent} onClose={() => setOpenEvent(null)} />
    </Page>
  );
}

/* ── Pieces ──────────────────────────────────────────────────────────────── */

/**
 * The band above the list: the filtered rows, counted by kind.
 *
 * Built from the same query as the list, so its total and the kicker's count
 * are one number by construction. Only the kinds present get a tile — thirteen
 * of them, eleven zero, would bury the two that are not. Nothing at all when
 * there is nothing to count: the empty state under it already says so.
 */
function KindSummary({ filters }: { filters: CompletedFilters }) {
  const { t } = useTranslation();
  const summary = useCompletedSummary(filters);

  if (summary.isPending) return null;
  if (summary.isError) {
    return (
      <ErrorState
        message={t("activities.summaryFailed")}
        onRetry={() => void summary.refetch()}
      />
    );
  }
  if (summary.data.total === 0) return null;

  return (
    <section aria-label={t("activities.summary")} className="mb-6">
      {/* Every tile carries its right and bottom rule and the list its top and
          left, so the grid stays closed however the tiles wrap. */}
      <dl className="flex flex-wrap border-t-2 border-l border-rule">
        <div className="min-w-[160px] flex-1 border-r border-b border-rule bg-ink px-4 py-3.5 text-ground">
          <dt className="kicker text-[10px] opacity-80">
            {t("activities.summaryTotal")}
          </dt>
          <dd className="mt-2 font-display text-[32px] leading-none font-extrabold tabular-nums">
            {summary.data.total.toLocaleString("es-MX")}
          </dd>
        </div>
        {summary.data.by_kind.map((row) => (
          <div
            key={row.kind}
            className="min-w-[160px] flex-1 border-r border-b border-rule px-4 py-3.5"
          >
            <dt className="flex items-center gap-1.5 text-[11px] text-mute-600">
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 shrink-0"
                style={{ background: CHANNEL_BAR[channelOf(row.kind)] }}
              />
              {t(`activityKindPlural.${row.kind}`)}
            </dt>
            <dd className="mt-2 font-display text-[24px] leading-none font-extrabold tabular-nums">
              {row.count.toLocaleString("es-MX")}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * A moment in a table cell: one line where the column has the width, and
 * otherwise broken between the day and the time and nowhere else. Left to
 * wrap freely, "27 ago 2026, 09:00 a.m." falls into four lines at tablet
 * width, and two date columns of that make every row four lines tall.
 */
function When({ iso }: { iso: string }) {
  return (
    <>
      <span className="whitespace-nowrap">{formatDate(iso)},</span>{" "}
      <span className="whitespace-nowrap">{formatTime(new Date(iso))}</span>
    </>
  );
}

/** The kind, behind the channel bar the agenda and the timeline draw (D24). */
function KindLabel({ activity }: { activity: Activity }) {
  return (
    <span className="flex items-center gap-2 font-semibold">
      <span
        aria-hidden="true"
        className="h-4 w-[3px] shrink-0"
        style={{ background: CHANNEL_BAR[channelOf(activity.kind)] }}
      />
      {activity.kind_display}
    </span>
  );
}

function Prospect({ activity }: { activity: Activity }) {
  const { t } = useTranslation();
  return (
    <>
      {activity.company ? (
        <Link to={`/prospectos/${activity.company}`} className="hover:underline">
          {activity.company_name}
        </Link>
      ) : (
        // Cold prospecting: the model allows a row with no company, and done
        // work on it is still done work.
        <span className="text-mute-600">{t("agenda.noCompany")}</span>
      )}
      {activity.contact_name ? (
        <div className="text-[11px] text-mute-600">{activity.contact_name}</div>
      ) : null}
    </>
  );
}

/** Who wrote it, and who it was handed to when that is someone else. */
function AgentName({ activity }: { activity: Activity }) {
  const { t } = useTranslation();
  // The author, as the agent filter reads it — never `current_agent`, which
  // is who holds the row now and would contradict the filter above it.
  const handedOver =
    activity.assigned_to_name &&
    activity.assigned_to_name !== activity.created_by_agent_name;
  return (
    <>
      {activity.created_by_agent_name ?? t("common.none")}
      {handedOver ? (
        <div className="text-[11px] text-mute-600 normal-case">
          {t("activities.assignedTo", { name: activity.assigned_to_name })}
        </div>
      ) : null}
    </>
  );
}

function Comment({ activity }: { activity: Activity }) {
  const { t } = useTranslation();
  return activity.comment ? (
    // Clamped, not cut: the list is for finding the row, and the whole
    // comment is one click away in the detail.
    <p className="line-clamp-2 break-words" title={activity.comment}>
      {activity.comment}
    </p>
  ) : (
    <span className="text-mute-600">{t("activities.noComment")}</span>
  );
}

function OpenButton({
  activity,
  onOpen,
}: {
  activity: Activity;
  onOpen: (activity: Activity) => void;
}) {
  const { t } = useTranslation();
  return (
    <Button
      className="px-3 py-[5px] text-xs"
      aria-label={t("activities.openLabel", { kind: activity.kind_display })}
      onClick={() => onOpen(activity)}
    >
      {t("activities.open")}
    </Button>
  );
}
